import { type FocalPoint } from '@ev-tech/search-modules-api';

import { type UploadcareImageTransformationProps } from '../components/Image/DefaultImageComponent';

type UploadcareImage = {
  focalPoint?: FocalPoint;
  height?: number;
  width?: number;
};

type GetUploadcareImageTransformationPropsParams = {
  aspectRatio: string;
  fit: 'automatic' | 'cover';
  image?: UploadcareImage;
};

const isValidDimension = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

const isValidFocalPointOffset = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100;

const getUploadcareAlignment = (focalPoint?: FocalPoint): string => {
  const x = focalPoint?.focalPointX;
  const y = focalPoint?.focalPointY;

  return isValidFocalPointOffset(x) && isValidFocalPointOffset(y) ? `${Math.round(x)}p,${Math.round(y)}p` : 'center';
};

export const getUploadcareImageTransformationProps = ({
  aspectRatio,
  fit,
  image,
}: GetUploadcareImageTransformationPropsParams): UploadcareImageTransformationProps => {
  const sourceWidth = Math.round(image?.width ?? 0);
  const sourceHeight = Math.round(image?.height ?? 0);
  const hasSourceDimensions = isValidDimension(sourceWidth) && isValidDimension(sourceHeight);

  if (fit === 'automatic' && !hasSourceDimensions) {
    return {};
  }

  const operation = fit === 'automatic' && hasSourceDimensions && sourceHeight > sourceWidth ? 'preview' : 'scale_crop';

  return {
    uploadcareAspectRatio: aspectRatio,
    uploadcareOperation: operation,
    ...(operation === 'scale_crop' && { uploadcareAlignment: getUploadcareAlignment(image?.focalPoint) }),
  };
};
